import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Zap, TrendingUp, DollarSign, Calendar,
  CheckCircle, Clock, XCircle, ChevronRight, Bell,
  Shield, RefreshCw} 
  from 'lucide-react';
import '../../../../assets/css/screens/serviceprovider/dashboard/subscription/SubscriptionHistory.css';
import WorkerNavbar from '../../../../components/serviceprovider/workerdashboard/Workernavbar/WorkerNavbar';


// ── Mock Data ──
const CURRENT_PLAN = {
  name: 'Pro Plan',
  price: 19.99,
  period: 'monthly',
  status: 'live',
  renewsOn: 'Jun 08, 2026',
  startDate: 'May 08, 2026',
  endDate: 'Jun 08, 2026',
  usedPercent: 63,
  daysLeft: 12,
  hoursLeft: 4,
  minutesLeft: 59,
  secondsLeft: 41,
  features: ['Unlimited bookings', 'Priority listing', 'Featured profile', '24/7 support'],
};

// const STATS = [
//   { label: 'Total Plans', value: 4,       icon: <Zap size={18}/>,         color: '#0491e1' },
//   { label: 'Total Spent', value: '$99.96', icon: <DollarSign size={18}/>,  color: '#10b981' },
//   { label: 'Days Left',   value: 12,       icon: <Calendar size={18}/>,    color: '#f59e0b' },
// ];

const HISTORY = [
  { id: 1, name: 'Starter', status: 'expired',   price: 9.99,  period: 'monthly', start: 'Mar 01, 2025', end: 'Mar 31, 2025', user: 'updatecard — INV-2026-001' },
  { id: 2, name: 'Elite',   status: 'cancelled', price: 49.99, period: 'monthly', start: 'Jan 01, 2025', end: 'Jan 15, 2025', user: 'jomcash wallet: INV-2025-003' },
  { id: 3, name: 'Pro',     status: 'expired',   price: 19.99, period: 'monthly', start: 'Nov 01, 2025', end: 'Nov 30, 2025', user: 'Vizia — INV-2026-101' },
];

const STATUS_CFG = {
  live:      { label: 'Live',      color: '#10b981', bg: 'rgba(16,185,129,0.12)'  },
  expired:   { label: 'Expired',   color: '#f59e0b', bg: 'rgba(245,158,11,0.12)'  },
  cancelled: { label: 'Cancelled', color: '#ef4444', bg: 'rgba(239,68,68,0.12)'   },
};

const HISTORY_TABS = ['All', 'Expired', 'Cancelled'];

// ── Countdown Timer ──
const useCountdown = (initial) => {
  const [time, setTime] = useState(initial);
  useEffect(() => {
    const id = setInterval(() => {
      setTime(prev => {
        let { days, hours, minutes, seconds } = prev;
        if (seconds > 0) return { ...prev, seconds: seconds - 1 };
        if (minutes > 0) return { ...prev, minutes: minutes - 1, seconds: 59 };
        if (hours > 0)   return { ...prev, hours: hours - 1, minutes: 59, seconds: 59 };
        if (days > 0)    return { ...prev, days: days - 1, hours: 23, minutes: 59, seconds: 59 };
        return prev;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);
  return time;
};

// ── Main Page ──
const SubscriptionHistory = () => {
  const navigate = useNavigate();
  const [historyTab, setHistoryTab] = useState('All');

  const time = useCountdown({
    days:    CURRENT_PLAN.daysLeft,
    hours:   CURRENT_PLAN.hoursLeft,
    minutes: CURRENT_PLAN.minutesLeft,
    seconds: CURRENT_PLAN.secondsLeft,
  });

  const filteredHistory = historyTab === 'All'
    ? HISTORY
    : HISTORY.filter(h => h.status === historyTab.toLowerCase());

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <>
    <WorkerNavbar/>
    <div className="sh-page">

      {/* ── MOBILE HEADER ── */}
      <div className="sh-mob-header d-md-none">
        <button className="sh-back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={20}/>
        </button>
        <div>
          <h4 className="sh-mob-title">My Subscriptions</h4>
          <p className="sh-mob-sub">Manage your membership plans</p>
        </div>
      </div>

      <div className="sh-container">

        {/* ── DESKTOP HEADER ── */}
        <div className="sh-desk-header">
          <div>
            <h2 className="sh-desk-title">Subscription History</h2>
            <p className="sh-desk-sub">Your complete record of plans and renewals</p>
          </div>
        </div>

        {/* ── STATS ROW ── */}
        {/* <div className="sh-stats-row">
          {STATS.map((s, i) => (
            <div key={i} className="sh-stat-card" style={{ '--sc': s.color }}>
              <div className="sh-stat-card__icon" style={{ color: s.color, background: `${s.color}15` }}>
                {s.icon}
              </div>
              <div className="sh-stat-card__val">{s.value}</div>
              <div className="sh-stat-card__label">{s.label}</div>
            </div>
          ))}
        </div> */}

        {/* ── CURRENT PLAN CARD ── */}
        <div className="sh-current-card">

          {/* Top row */}
          <div className="sh-current-card__top">
            <div className="sh-current-card__left">
              <div className="sh-current-card__icon">
                <Zap size={20}/>
              </div>
              <div>
                <div className="sh-current-card__name-row">
                  <span className="sh-current-name">{CURRENT_PLAN.name}</span>
                  <span className="sh-live-badge">
                    <span className="sh-live-dot"/>
                    Live
                  </span>
                </div>
                <p className="sh-current-renews">Renews on {CURRENT_PLAN.renewsOn}</p>
              </div>
            </div>
            <div className="sh-current-card__price-wrap">
              <span className="sh-current-price">${CURRENT_PLAN.price}</span>
              <span className="sh-current-period">/ {CURRENT_PLAN.period}</span>
            </div>
          </div>

          {/* Countdown */}
          <div className="sh-countdown-label">Expires in</div>
          <div className="sh-countdown">
            {[
              { val: pad(time.days),    label: 'DAYS' },
              { val: pad(time.hours),   label: 'HOURS' },
              { val: pad(time.minutes), label: 'MINUTES' },
              { val: pad(time.seconds), label: 'SECONDS' },
            ].map((item, i) => (
              <div key={i} className="sh-countdown-unit">
                <span className="sh-countdown-val">{item.val}</span>
                <span className="sh-countdown-lbl">{item.label}</span>
              </div>
            ))}
          </div>

          {/* Progress bar */}
          <div className="sh-progress-wrap">
            <span className="sh-progress-start">{CURRENT_PLAN.startDate}</span>
            <div className="sh-progress-bar">
              <div
                className="sh-progress-fill"
                style={{ width: `${CURRENT_PLAN.usedPercent}%` }}
              />
            </div>
            <span className="sh-progress-end">{CURRENT_PLAN.endDate}</span>
          </div>
          <div className="sh-progress-pct">{CURRENT_PLAN.usedPercent}% used</div>

          {/* Features */}
          <div className="sh-current-features">
            {CURRENT_PLAN.features.map((f, i) => (
              <span key={i} className="sh-feature-item">
                <CheckCircle size={13} className="sh-feature-check"/>
                {f}
              </span>
            ))}
          </div>

          {/* Actions */}
          <div className="sh-current-actions">
            <button
              className="sh-upgrade-btn"
              onClick={() => navigate('/workersubscription')}
            >
              <RefreshCw size={14}/> Upgrade Plan
            </button>
            <button className="sh-manage-btn">Manage</button>
          </div>

        </div>

        {/* ── MOBILE: Upgrade Banner ── */}
        {/* <button className="sh-upgrade-banner d-md-none" onClick={() => navigate('/workersubscription')}>
          <div className="sh-upgrade-banner__icon">
            <RefreshCw size={18}/>
          </div>
          <div className="sh-upgrade-banner__text">
            <span className="sh-upgrade-banner__title">Want to upgrade or change your plan?</span>
            <span className="sh-upgrade-banner__sub">Explore all premium plans and pick what suits you best.</span>
          </div>
          <ChevronRight size={16} className="sh-upgrade-banner__arrow"/>
        </button> */}

        {/* ── PAST RECORDS ── */}
        <div className="sh-history-section">
          <div className="sh-history-header">
            <h5 className="sh-history-title">
              Past Records
              <span className="sh-history-title-sub d-none d-md-inline"> — view full record →</span>
            </h5>
            <div className="sh-history-tabs">
              {HISTORY_TABS.map(t => (
                <button
                  key={t}
                  className={`sh-history-tab ${historyTab === t ? 'active' : ''}`}
                  onClick={() => setHistoryTab(t)}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="sh-history-list">
            {filteredHistory.length === 0 ? (
              <div className="sh-history-empty">No records found.</div>
            ) : filteredHistory.map((h) => {
              const st = STATUS_CFG[h.status] || STATUS_CFG.expired;
              return (
                <div key={h.id} className="sh-history-item">
                  <div className="sh-history-item__left">
                    <div className="sh-history-item__icon">
                      <Zap size={16}/>
                    </div>
                    <div className="sh-history-item__info">
                      <div className="sh-history-item__name-row">
                        <span className="sh-history-item__name">{h.name}</span>
                        <span
                          className="sh-history-item__status"
                          style={{ color: st.color, background: st.bg }}
                        >
                          {h.status === 'expired' ? <Clock size={10}/> : <XCircle size={10}/>}
                          {st.label}
                        </span>
                      </div>
                      <span className="sh-history-item__dates">{h.start} — {h.end}</span>
                      <span className="sh-history-item__meta">{h.user}</span>
                    </div>
                  </div>
                  <div className="sh-history-item__right">
                    <span className="sh-history-item__price">${h.price}</span>
                    <span className="sh-history-item__period">{h.period}</span>
                  </div>
                </div>
              );
            })}
          </div>

        </div>

        {/* Footer */}
        <p className="sh-footer-note">
          <Shield size={13}/> Encrypted records · Cancel anytime from settings
        </p>

      </div>
    </div>
    </>
  );
};

export default SubscriptionHistory;